import styled from "@emotion/styled";
import {
	Box,
	AppBar,
	Toolbar,
	Typography,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Theme,
	Stack,
	IconButton,
	Avatar,
	Menu,
	MenuItem,
	Divider,
	ClickAwayListener,
} from "@mui/material";
import {
	FC,
	PropsWithChildren,
	createContext,
	useContext,
	useEffect,
	useState,
} from "react";
import ClientIcon from "@mui/icons-material/Group";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";
import { AppRouting, getPath } from "Infrastructure/Utils/UrlUtils";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useSignOut } from "Auth/Hooks/useSignOut";
import { Logo } from "Shared/Logo";
import DnsIcon from "@mui/icons-material/Dns";
import ListAltIcon from "@mui/icons-material/ListAlt";
import React from "react";

import ArticleIcon from "@mui/icons-material/Article";
import { capitalizeFirstLetterOfString } from "Infrastructure/Utils/StringUtils";
import { UserInfoProvider } from "Shared/Contexts/UserInfoContext";
import { UserInfo } from "Infrastructure/Api/Api";
import { isTokenExpired } from "Infrastructure/Utils/TrackingUtils";

type Props = {
	title: string;
};

const drawerWidth = 240;

const StyledBox = styled(Box)`
	display: flex;
	min-height: 100vh;
	/* background-color: #fcfcfc; */
`;

const StyledAppBar = styled(AppBar)(({ theme }: { theme: Theme }) => {
	return `
	box-shadow: 0 0px 2px 2px gray;
  z-index: ${theme.zIndex.drawer + 1};
	background-image: linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${
		theme.palette.primary.main
	} 100%);
`;
});

const StyledDrawer = styled(Drawer)<{ expanded: boolean }>`
	position: fixed;
	top: 0;
	left: 0;
	height: 100vh;
	z-index: 9;

	width: ${({ expanded }) => (expanded ? `${drawerWidth}px` : "56px")};
	white-space: nowrap;
	overflow-x: hidden;
	transition: width 0.3s;

	& .MuiDrawer-paper {
		position: static;
		width: ${({ expanded }) => (expanded ? `${drawerWidth}px` : "56px")};
		box-sizing: border-box;
		overflow-x: hidden;
		transition: width 0.3s;
	}
`;

const StyledMainContent = styled(Box)`
	flex-grow: 1;
	padding: 16px;
`;

const StyledListItem = styled(ListItem)`
	padding: 0;
`;

const StyledListItemButton = styled(ListItemButton)`
	overflow: hidden;
	height: 40px;
	padding-left: 8px;
	padding-right: 8px;
`;

type AuthenticatedLayoutContextType = {
	isMenuShown: boolean;
};

export const AuthenticatedLayoutContext =
	createContext<AuthenticatedLayoutContextType>({
		isMenuShown: false,
	});

type AccountMenuProps = {
	userName: string;
	userLogin: string;
	userRoles: string;
	onSignOut: () => void;
};

const AccountMenu: FC<AccountMenuProps> = ({
	userName,
	userLogin,
	userRoles,
	onSignOut,
}) => {
	const { t } = useResource();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const signOutInMenu = () => {
		handleClose();
		onSignOut();
	};

	const openHelpLink = () => {
		handleClose();
		window.open(
			"https://blogic.atlassian.net/wiki/spaces/NSUR/pages/3468886022",
			"_blank"
		);
	};

	return (
		<React.Fragment>
			<Box
				sx={{ display: "flex", alignItems: "center", textAlign: "center" }}
			>
				<IconButton
					onClick={handleClick}
					disableRipple
					size="small"
					sx={{ ml: 2 }}
					aria-controls={open ? "account-menu" : undefined}
					aria-haspopup="true"
					aria-expanded={open ? "true" : undefined}
				>
					<Avatar sx={{ width: 32, height: 32 }} />
				</IconButton>
			</Box>
			<Menu
				anchorEl={anchorEl}
				id="account-menu"
				open={open}
				onClose={handleClose}
				onClick={handleClose}
				disableScrollLock={true}
				slotProps={{
					paper: {
						elevation: 0,
						sx: {
							overflow: "visible",
							filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
							mt: 1.5,
							"& .MuiAvatar-root": {
								width: 32,
								height: 32,
								ml: -0.5,
								mr: 1,
							},
							"&::before": {
								content: '""',
								display: "block",
								position: "absolute",
								top: 0,
								right: 14,
								width: 10,
								height: 10,
								bgcolor: "background.paper",
								transform: "translateY(-50%) rotate(45deg)",
								zIndex: 0,
							},
						},
					},
				}}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
			>
				<Stack mb={1} mx={2} alignItems="center">
					<Typography variant="h2" mt={1}>
						{userName}
					</Typography>
					<Typography variant="caption">{userLogin}</Typography>
					<Typography variant="caption" color="#a7a7a7">
						{userRoles}
					</Typography>
				</Stack>

				<Divider sx={{ mb: 1 }} />

				<MenuItem onClick={openHelpLink}>
					<ListItemIcon>
						<ArticleIcon fontSize="small" />
					</ListItemIcon>
					{"Dokumentace"}
				</MenuItem>
				<Divider />
				<MenuItem onClick={signOutInMenu}>
					<ListItemIcon>
						<LogoutIcon fontSize="small" />
					</ListItemIcon>
					{t(Resources.Common.SignOut)}
				</MenuItem>
			</Menu>
		</React.Fragment>
	);
};

export const AuthenticatedLayout: FC<PropsWithChildren<Props>> = ({
	title,
	children,
}) => {
	const { t } = useResource();
	const { isMenuShown } = useContext(AuthenticatedLayoutContext);
	const [isDrawerExpanded, setIsDrawerExpanded] = useState<boolean>(false);

	const toggleDrawer = () => {
		setIsDrawerExpanded((prev: boolean) => !prev);
	};

	useEffect(() => {
		const instanceType = capitalizeFirstLetterOfString(
			import.meta.env.VITE_NAME as string
		);
		document.title = `${instanceType} ${t(
			Resources.Common.AppName
		)} | ${title}`;
	}, [title, t]);

	const menuItems = [
		{
			text: "Správa instancí",
			icon: <DnsIcon />,
			to: getPath(AppRouting.InstanceManagement),
		},
	];

	const { data } = useUserInfoQuery();

	if (
		data?.status === 200 &&
		data.data.accessRightCodes.some((x) => x === "CodelistManagement")
	) {
		menuItems.push({
			text: "Správa číselníků",
			icon: <ListAltIcon />,
			to: getPath(AppRouting.CodelistManagement),
		});
	}

	if (
		data?.status === 200 &&
		data.data.accessRightCodes.some((x) => x === "UserManagement")
	) {
		menuItems.push({
			text: "Uživatelé",
			icon: <ClientIcon />,
			to: getPath(AppRouting.Users),
		});
	}

	const userInfo = isUserInfo(data?.data) ? data.data : null;
	const userLogin = data?.status === 200 ? data.data.login : "";
	const userName =
		data?.status === 200
			? data.data.firstName === data.data.lastName
				? data.data.firstName
				: `${data.data.firstName} ${data.data.lastName}`
			: "";
	const userRoles =
		data?.status === 200 ? data.data.accessRoles.join(", ") : "";
	const signOut = useSignOut();

	useEffect(() => {
		const checkTokenExpiration = () => {
			if (isTokenExpired()) {
				signOut();
			}
		};

		checkTokenExpiration();
		const interval = setInterval(checkTokenExpiration, 60000); // Check every minute

		return () => clearInterval(interval);
	}, [signOut]);

	function isUserInfo(data: any): data is UserInfo {
		return (
			data &&
			typeof data.token === "string" &&
			typeof data.userID === "number" &&
			typeof data.login === "string"
		);
	}

	return data?.data ? (
		<UserInfoProvider userInfo={userInfo}>
			<StyledBox>
				{isMenuShown && (
					<div>
						<ClickAwayListener onClickAway={() => setIsDrawerExpanded(false)}>
							<div>
								<StyledAppBar position="fixed">
									<Toolbar disableGutters sx={{ pr: "30px", pl: "20px" }}>
										<IconButton
											onClick={toggleDrawer}
											edge="start"
											color="inherit"
											sx={{ mr: 2 }}
											disableRipple
										>
											<MenuIcon />
										</IconButton>
										<UnstyledLink to={getPath(AppRouting.Dashboard)}>
											<Stack
												paddingRight={1}
												borderRight="1px solid white"
												marginRight={1}
												direction="row"
												alignItems="center"
											>
												<Logo />
											</Stack>
										</UnstyledLink>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center"
											width="100%"
										>
											<Typography variant="h6" noWrap component="div">
												{title}
											</Typography>
											<Stack
												direction={"row"}
												justifyContent="flex-end"
												alignItems="center"
												spacing={2}
											>
												<AccountMenu
													userName={userName}
													userLogin={userLogin}
													userRoles={userRoles}
													onSignOut={signOut}
												/>
											</Stack>
										</Stack>
									</Toolbar>
								</StyledAppBar>

								<StyledDrawer variant="permanent" expanded={isDrawerExpanded}>
									<Toolbar />
									<Box
										sx={{
											overflow: "auto",
											width: isDrawerExpanded ? drawerWidth : 56,
										}}
									>
										<List>
											{menuItems.map((item) => (
												<UnstyledLink to={item.to} key={item.text}>
													<StyledListItem disablePadding>
														<StyledListItemButton>
															<ListItemIcon sx={{ ml: 0.6 }}>
																{item.icon}
															</ListItemIcon>
															{isDrawerExpanded && (
																<ListItemText primary={item.text} />
															)}
														</StyledListItemButton>
													</StyledListItem>
												</UnstyledLink>
											))}
										</List>
									</Box>
								</StyledDrawer>
							</div>
						</ClickAwayListener>
					</div>
				)}

				<StyledMainContent component="main">
					<Toolbar />
					<Box sx={{ ml: "56px" }}>{children}</Box>
				</StyledMainContent>
			</StyledBox>
		</UserInfoProvider>
	) : (
		<div>Loading...</div>
	);
};
